import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Button,
  DialogComponentProps,
  TextField,
  Typography,
} from "@platform/design-system";
import { useCoverageNoteDrafts } from "@platform/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type NotesTab } from "./constants";

import type { CoverageNoteSection } from "./api/types";

interface NotesDrawerData {
  ecoRfpId: string;
  selectedTab?: NotesTab;
  rfpNotes?: string;
  initialCoverageNotes?: Record<string, string>;
  coverages?: CoverageNoteSection[];
}

interface NotesTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
}

export const NotesTextArea = ({
  value,
  onChange,
  placeholder,
  maxLength = 4096,
}: NotesTextAreaProps): React.JSX.Element => {
  const [localValue, setLocalValue] = useState(value);
  const previousValueRef = useRef(value);

  useEffect(() => {
    if (value !== previousValueRef.current) {
      previousValueRef.current = value;
      setLocalValue(value);
    }
  }, [value]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      previousValueRef.current = newValue;
      setLocalValue(newValue);
      onChange(newValue);
    }
  };

  return (
    <Box>
      <TextField
        multiline
        rows={14}
        fullWidth
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        inputProps={{ maxLength }}
      />
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: "block", textAlign: "right", mt: 0.5 }}
      >
        {localValue.length} / {maxLength}
      </Typography>
    </Box>
  );
};

interface AnchorListProps {
  items: readonly { id: string; label: string }[];
  activeId: string;
  onItemClick: (id: string) => void;
}

const AnchorList = ({
  items,
  activeId,
  onItemClick,
}: AnchorListProps): React.JSX.Element => (
  <List dense sx={{ py: 0 }}>
    {items.map((item) => (
      <ListItemButton
        key={item.id}
        selected={activeId === item.id}
        onClick={() => onItemClick(item.id)}
        sx={{
          mb: 0.5,
          py: 0.5,
          borderLeft: "2px solid transparent",
          borderRadius: 0,
          "&.Mui-selected": {
            backgroundColor: "transparent",
            borderLeftColor: "brandSupporting.main",
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        }}
      >
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            variant: "body2",
            color:
              activeId === item.id ? "brandSupporting.main" : "text.secondary",
            fontWeight: activeId === item.id ? "medium" : "regular",
          }}
        />
      </ListItemButton>
    ))}
  </List>
);

interface NotesDrawerContentProps extends NotesDrawerData {
  onClose?: DialogComponentProps["onClose"];
}

const NotesDrawerContent = ({
  ecoRfpId,
  selectedTab = "rfp",
  rfpNotes = "",
  initialCoverageNotes = {},
  coverages = [],
  onClose,
}: NotesDrawerContentProps): React.JSX.Element => {
  const [activeTab, setActiveTab] = useState<NotesTab>(selectedTab);
  const [rfpNotesValue, setRfpNotesValue] = useState(rfpNotes);
  const [activeCoverageId, setActiveCoverageId] = useState<string>(
    coverages[0]?.noteKey ?? "",
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isScrollingProgrammatically = useRef(false);
  const shouldRestoreDraftsRef = useRef(true);
  const noteDraftCoverages = useMemo(
    () =>
      coverages.map((coverage) => ({
        id: coverage.coverageId,
        benefitPlanType: coverage.benefitPlanType,
        noteKey: coverage.noteKey,
      })),
    [coverages],
  );

  const {
    notes: coverageNotesValues,
    setCoverageNote,
    setCoverageNotes,
  } = useCoverageNoteDrafts({
    ecoRfpId,
    coverages: noteDraftCoverages,
    enabled: coverages.length > 0,
  });

  useEffect(
    () => () => {
      if (shouldRestoreDraftsRef.current) {
        setCoverageNotes(initialCoverageNotes);
      }
    },
    [initialCoverageNotes, setCoverageNotes],
  );

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: NotesTab,
  ): void => {
    setActiveTab(newValue);
  };

  const handleCoverageNoteChange = (noteKey: string, value: string): void => {
    setCoverageNote(noteKey, value);
  };

  const handleCancel = (): void => {
    setCoverageNotes(initialCoverageNotes);
    shouldRestoreDraftsRef.current = false;
    onClose?.();
  };

  const handleAnchorClick = (noteKey: string): void => {
    const section = sectionRefs.current[noteKey];
    if (section && scrollContainerRef.current) {
      isScrollingProgrammatically.current = true;
      setActiveCoverageId(noteKey);
      section.scrollIntoView({ behavior: "smooth", block: "start" });

      // Re-enable scrollspy after scroll animation completes
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500);
    }
  };

  // Scrollspy: update active coverage based on scroll position
  const handleScroll = useCallback(() => {
    // Skip scrollspy updates during programmatic scrolling
    if (isScrollingProgrammatically.current) return;
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const containerTop = container.getBoundingClientRect().top;

    let closestId = coverages[0]?.noteKey ?? "";
    let closestDistance = Infinity;

    for (const coverage of coverages) {
      const section = sectionRefs.current[coverage.noteKey];
      if (section) {
        const sectionTop = section.getBoundingClientRect().top;
        const distance = Math.abs(sectionTop - containerTop);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestId = coverage.noteKey;
        }
      }
    }

    setActiveCoverageId(closestId);
  }, [coverages]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && activeTab === "coverages") {
      container.addEventListener("scroll", handleScroll);
      return (): void => container.removeEventListener("scroll", handleScroll);
    }
    return undefined;
  }, [activeTab, handleScroll]);

  const handleSave = (): void => {
    shouldRestoreDraftsRef.current = false;
    onClose?.({ rfpNotes: rfpNotesValue, coverageNotes: coverageNotesValues });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            mb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body-large" component="h2">
            <strong>Edit Notes</strong>
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="Notes tabs"
          sx={{
            px: 2,
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTabs-indicator": {
              height: 2,
              backgroundColor: "brandPrimary.main",
            },
            "& .MuiTab-root.Mui-selected": { color: "brandPrimary.main" },
          }}
        >
          <Tab label="RFP" value="rfp" />
          <Tab
            label="Coverages"
            value="coverages"
            disabled={coverages.length === 0}
          />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ px: 3, pt: 3 }}>
          {activeTab === "rfp" && (
            <Box>
              <Typography
                variant="body-medium"
                color="textSecondary"
                sx={{ mb: 2 }}
              >
                Enter notes specific to this RFP
              </Typography>
              <NotesTextArea
                value={rfpNotesValue}
                onChange={setRfpNotesValue}
                placeholder="Enter RFP notes..."
                maxLength={4096}
              />
            </Box>
          )}

          {activeTab === "coverages" && coverages.length === 0 && (
            <Typography variant="body-medium" color="textSecondary">
              No coverages available
            </Typography>
          )}

          {activeTab === "coverages" && coverages.length > 0 && (
            <Box
              sx={{ display: "flex", gap: 3, height: "calc(100vh - 300px)" }}
            >
              {/* Scrollable notes sections on the left */}
              <Box
                ref={scrollContainerRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  pr: 2,
                }}
              >
                {coverages.map((coverage) => (
                  <Box
                    key={coverage.noteKey}
                    ref={(el: HTMLDivElement | null) => {
                      sectionRefs.current[coverage.noteKey] = el;
                    }}
                    sx={{ mb: 4 }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1 }}
                      color="textSecondary"
                    >
                      <strong>{coverage.label}</strong>
                    </Typography>
                    <NotesTextArea
                      value={coverageNotesValues[coverage.noteKey] ?? ""}
                      onChange={(value) =>
                        handleCoverageNoteChange(coverage.noteKey, value)
                      }
                      placeholder={`Enter ${coverage.label} notes...`}
                      maxLength={1024}
                    />
                  </Box>
                ))}
              </Box>

              {/* AnchorList on the right */}
              <Box
                sx={{
                  width: 160,
                  flexShrink: 0,
                  position: "sticky",
                  top: 0,
                  alignSelf: "flex-start",
                }}
              >
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Jump to
                </Typography>
                <AnchorList
                  items={coverages.map((coverage) => ({
                    id: coverage.noteKey,
                    label: coverage.label,
                  }))}
                  activeId={activeCoverageId}
                  onItemClick={handleAnchorClick}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: "flex-end", px: 3, pb: 3, pt: 2 }}
      >
        <Button variant="tertiary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Stack>
    </Box>
  );
};

const NotesDrawer = ({
  data,
  onClose,
}: DialogComponentProps): React.JSX.Element => {
  const drawerData =
    (data as unknown as NotesDrawerData | undefined) ?? undefined;

  if (!drawerData?.ecoRfpId) {
    return <></>;
  }

  return <NotesDrawerContent {...drawerData} onClose={onClose} />;
};

export default NotesDrawer;
